import { type FC, useEffect, useMemo, useState } from 'react';
import clsx from 'clsx';
import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card/Card';
import { Input } from '@/components/ui/input';
import { SectionLabel } from '@/components/ui/section-label';
import {
  Select,
  SelectContent,
  SelectGroup,
  SelectItem,
  SelectLabel,
  SelectSeparator,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { useTranslation } from '@/hooks/useTranslation';
import type { InfluxSeriesQuery, SignalMachinesList, SignalTarget } from '../type/SignalsTypes';
import { MachineSourceHint, MissingSignalSource, SignalSourceLine } from './SignalSourceLine';
import { useInfluxSignals, useInfluxMachineStatus } from '../hooks/useInfluxMachineData';
import { defaultCheckedSignals, SIGNALS_MIN, SIGNALS_MAX } from '../lib/signalGroups';
import {
  RELATIVE_CHIPS,
  resolveRelativeRange,
  validateCustomRange,
  customRangeToIso,
  lastActivityRange,
  type RelativeChipKey,
  type TimeMode,
} from '../lib/signalTimeRange';
import { SignalGroupPicker } from './SignalGroupPicker';
import { SignalsTimeRangeSelector } from './SignalsTimeRangeSelector';

interface SignalsContextPanelProps {
  /** The caller's machines with their signal source (+ unlinked sources for admins). */
  machines: SignalMachinesList | null;
  machinesLoading: boolean;
  onLoad: (query: InfluxSeriesQuery) => void;
}

const DEFAULT_RELATIVE_CHIP: RelativeChipKey = '1h';

export const SignalsContextPanel: FC<SignalsContextPanelProps> = ({
  machines,
  machinesLoading,
  onLoad,
}) => {
  const { t } = useTranslation();

  // `machine:<id>` for a machine, `source:<name>` for an admin's unlinked source.
  const [selectedKey, setSelectedKey] = useState<string | null>(null);
  const [checkedSignals, setCheckedSignals] = useState<Set<string>>(new Set());
  const [timeMode, setTimeMode] = useState<TimeMode>('relative');
  const [relativeChip, setRelativeChip] = useState<RelativeChipKey>(DEFAULT_RELATIVE_CHIP);
  const [customStart, setCustomStart] = useState('');
  const [customEnd, setCustomEnd] = useState('');
  const [toolId, setToolId] = useState('');

  const selectedMachine =
    machines?.machines.find((machine) => `machine:${machine.id}` === selectedKey) ?? null;
  const selectedSource =
    machines?.unlinkedSources.find((source) => `source:${source.name}` === selectedKey) ?? null;
  // What the Influx queries read; null for a machine without a signal source.
  const machineIdToRead = selectedMachine?.source ? selectedMachine.id : null;
  const sourceToRead = selectedSource?.name ?? null;
  const target = useMemo<SignalTarget | null>(() => {
    if (machineIdToRead !== null) return { machineId: machineIdToRead };
    if (sourceToRead !== null) return { machine: sourceToRead };
    return null;
  }, [machineIdToRead, sourceToRead]);
  // The worker could not read the database: the source tags would all say "not in the
  // database", so they are left out.
  const sourceStatusKnown = Boolean(machines?.influxAvailable);
  const shownSource = selectedMachine?.source ?? selectedSource;
  const unlinkedSources = machines?.unlinkedSources ?? [];

  const { data: signals, isLoading: signalsLoading } = useInfluxSignals(target);
  const { lastTime } = useInfluxMachineStatus(target);

  // Reset to the default core selection whenever the fetched signal list changes (new
  // machine, or a refetch) — mirrors SignalsChartCard's chip-reset-on-refetch pattern.
  const signalsKey = signals?.join('|') ?? '';
  useEffect(() => {
    setCheckedSignals(new Set(defaultCheckedSignals(signals ?? [])));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [signalsKey]);

  function toggleSignal(tag: string) {
    setCheckedSignals((prev) => {
      if (prev.has(tag)) {
        if (prev.size <= SIGNALS_MIN) return prev; // min-1 rule
        const next = new Set(prev);
        next.delete(tag);
        return next;
      }
      if (prev.size >= SIGNALS_MAX) return prev; // worker cap
      const next = new Set(prev);
      next.add(tag);
      return next;
    });
  }

  const rangeError = timeMode === 'custom' ? validateCustomRange(customStart, customEnd) : null;
  const canLoad =
    target !== null && checkedSignals.size > 0 && (timeMode === 'relative' || rangeError === null);

  function handleLoad() {
    if (target === null || checkedSignals.size === 0) return;

    const range =
      timeMode === 'relative'
        ? resolveRelativeRange(RELATIVE_CHIPS.find((c) => c.key === relativeChip)!.ms)
        : validateCustomRange(customStart, customEnd) === null
          ? customRangeToIso(customStart, customEnd)
          : null;
    if (!range) return;

    onLoad({
      target,
      signals: [...checkedSignals],
      start: range.start,
      end: range.end,
      ...(toolId.trim() ? { toolId: toolId.trim() } : {}),
    });
  }

  function handleUseLastActivity() {
    if (!lastTime) return;
    const { start, end } = lastActivityRange(lastTime);
    setCustomStart(start);
    setCustomEnd(end);
    setTimeMode('custom');
  }

  return (
    <Card className="flex flex-col gap-4">
      <SectionLabel>{t('signals.panel.badge')}</SectionLabel>

      <div className="flex flex-col gap-1.5">
        <label className="text-sm font-medium text-foreground">{t('signals.panel.machineLabel')}</label>
        <Select value={selectedKey ?? ''} onValueChange={setSelectedKey} disabled={machinesLoading}>
          <SelectTrigger className="w-full" data-testid="signals-machine-select-trigger">
            <SelectValue placeholder={t('signals.panel.machinePlaceholder')}>
              {selectedMachine?.name ?? selectedSource?.name}
            </SelectValue>
          </SelectTrigger>
          <SelectContent>
            {(machines?.machines ?? []).map((machine) => (
              <SelectItem key={machine.id} value={`machine:${machine.id}`}>
                <span className="flex items-center gap-2">
                  <span>{machine.name}</span>{' '}
                  <MachineSourceHint source={machine.source?.name ?? null} />
                </span>
              </SelectItem>
            ))}
            {unlinkedSources.length > 0 && (
              <>
                <SelectSeparator />
                <SelectGroup>
                  <SelectLabel>{t('signals.panel.unlinkedSources')}</SelectLabel>
                  {unlinkedSources.map((source) => (
                    <SelectItem key={source.name} value={`source:${source.name}`}>
                      <span className="font-mono text-[13px]">{source.name}</span>
                    </SelectItem>
                  ))}
                </SelectGroup>
              </>
            )}
          </SelectContent>
        </Select>
        {selectedMachine && !selectedMachine.source && (
          <MissingSignalSource message={t('signals.panel.noSignalSource')} />
        )}
        {shownSource && <SignalSourceLine source={shownSource} showTags={sourceStatusKnown} />}
        {target && (
          <p className="text-xs text-muted-foreground" data-testid="machine-status-line">
            {lastTime
              ? t('signals.panel.machineStatus.lastActivity', { date: new Date(lastTime).toLocaleString() })
              : t('signals.panel.machineStatus.noData')}
          </p>
        )}
      </div>

      {/* A machine without a source: the warning above says why there is nothing to pick. */}
      {(target !== null || selectedKey === null) && (
        <SignalGroupPicker
          signals={signals ?? []}
          loading={signalsLoading}
          machineSelected={target !== null}
          checked={checkedSignals}
          onToggle={toggleSignal}
        />
      )}

      <fieldset
        disabled={target === null}
        className={clsx('m-0 flex min-w-0 flex-col gap-4 border-0 p-0', target === null && 'opacity-50')}
      >
        <SignalsTimeRangeSelector
          mode={timeMode}
          relativeChip={relativeChip}
          onSelectRelative={(key) => {
            setRelativeChip(key);
            setTimeMode('relative');
          }}
          onSelectCustom={() => setTimeMode('custom')}
          customStart={customStart}
          customEnd={customEnd}
          onCustomStartChange={setCustomStart}
          onCustomEndChange={setCustomEnd}
          rangeError={rangeError}
          lastTime={lastTime}
          onUseLastActivity={handleUseLastActivity}
        />

        <div className="flex flex-col gap-1.5">
          <label htmlFor="signals-tool-id" className="text-sm font-medium text-foreground">
            {t('signals.panel.toolIdLabel')}
          </label>
          <Input
            id="signals-tool-id"
            value={toolId}
            onChange={(e) => setToolId(e.target.value)}
            placeholder={t('signals.panel.toolIdPlaceholder')}
          />
        </div>
      </fieldset>

      <Button onClick={handleLoad} disabled={!canLoad} data-testid="load-signals-button">
        {t('signals.panel.loadButton')}
      </Button>
    </Card>
  );
};
